import type { LayElementInput } from "../utils/canvasLayoutFindings";
import type { CanvasBuilderElement } from "./canvasBuilderProjection";

export function getCanvasBuilderContainerVariant(
  element: CanvasBuilderElement,
): string | undefined {
  if (element.type !== "container") {
    return element.props.variant as string | undefined;
  }
  const containerType =
    (element.props.containerType as string | undefined) ||
    (element.props.variant as string | undefined);
  return containerType?.toLowerCase();
}

export function getCanvasBuilderChildren(
  elements: CanvasBuilderElement[],
  parentId: string,
): CanvasBuilderElement[] {
  return elements
    .filter((element) => element.parentElementId === parentId)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

/** Remove a builder element and every nested descendant from the draft. */
export function removeCanvasBuilderElementSubtree(
  elements: CanvasBuilderElement[],
  rootId: string,
): CanvasBuilderElement[] {
  const removedIds = new Set([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const element of elements) {
      if (
        element.parentElementId &&
        removedIds.has(element.parentElementId) &&
        !removedIds.has(element.tempId)
      ) {
        removedIds.add(element.tempId);
        changed = true;
      }
    }
  }
  return elements.filter((element) => !removedIds.has(element.tempId));
}

export function getCanvasBuilderInternalGridColumns(
  element: CanvasBuilderElement,
): number {
  const internalGrid = element.props.internalGrid as
    { columns?: number } | undefined;
  const columns = internalGrid?.columns;
  return typeof columns === "number" && Number.isInteger(columns) && columns > 0
    ? columns
    : 12;
}

/** Return a user-facing error when a nested child position is invalid. */
export function getNestedChildPositionError(
  elements: CanvasBuilderElement[],
  elementId: string,
  row: number,
  column: number,
): string | null {
  const element = elements.find((candidate) => candidate.tempId === elementId);
  if (!element?.parentElementId) return "Select a child inside a container.";
  if (!Number.isInteger(row) || row < 1) return "Row must be 1 or greater.";
  if (!Number.isInteger(column) || column < 1)
    return "Column must be 1 or greater.";

  const parent = elements.find(
    (candidate) => candidate.tempId === element.parentElementId,
  );
  if (!parent) return "The parent container could not be found.";

  if (row + element.rowSpan - 1 > parent.rowSpan) {
    return `This child extends past the ${parent.rowSpan}-row parent grid.`;
  }

  const columns = getCanvasBuilderInternalGridColumns(parent);
  if (column + element.columnSpan - 1 > columns) {
    return `This child extends past the ${columns}-column parent grid.`;
  }

  const overlapsSibling = elements.some((sibling) => {
    if (
      sibling.tempId === elementId ||
      sibling.parentElementId !== element.parentElementId ||
      sibling.parentPanelId !== element.parentPanelId
    ) {
      return false;
    }
    const separated =
      column + element.columnSpan <= sibling.gridColumn ||
      sibling.gridColumn + sibling.columnSpan <= column ||
      row + element.rowSpan <= sibling.gridRow ||
      sibling.gridRow + sibling.rowSpan <= row;
    return !separated;
  });

  return overlapsSibling
    ? "This position overlaps another child in the same parent grid."
    : null;
}

export function projectCanvasLayoutInputs(
  elements: CanvasBuilderElement[],
): LayElementInput[] {
  const elementById = new Map(
    elements.map((element) => [element.tempId, element]),
  );
  return elements.map((element) => {
    const parent = element.parentElementId
      ? elementById.get(element.parentElementId)
      : undefined;
    const internalGrid = parent?.props.internalGrid as
      { columns?: number } | undefined;
    return {
      id: element.tempId,
      type: element.type,
      gridColumn: element.gridColumn,
      gridRow: element.gridRow,
      columnSpan: element.columnSpan,
      rowSpan: element.rowSpan,
      parentElementId: element.parentElementId,
      parentPanelId: element.parentPanelId,
      parentGridColumns: internalGrid?.columns,
    };
  });
}
